/**
 * Per-day shift override (employee selects shift before check-in when policy allows).
 */

import { getLocalDayKey } from "./attendancePunchStorage";

const SHIFTS_STORAGE_KEY = "hrms_frontend_attendance_shifts_v1";

function loadShiftsLocal() {
  try {
    const raw = localStorage.getItem(SHIFTS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Active shifts saved in Attendance → Shifts (for Change Shift dropdowns). */
export function loadAttendanceShifts() {
  return loadShiftsLocal().filter((s) => s && s.status !== "inactive");
}

/** Options for Current / New Shift selects: value = shift name (engine resolves by name). */
export function listAttendanceShiftSelectOptions() {
  return loadAttendanceShifts()
    .map((s) => {
      const name = String(s?.name || "").trim();
      if (!name) return null;
      const start = s.startTime || s.flexiBandwidthStart || "";
      const end = s.endTime || s.flexiBandwidthEnd || "";
      const isFlexi = s.shiftType === "flexi" || s.flexiEnabled;
      const window =
        start && end ? `${start}–${end}` : isFlexi ? "Flexi" : "";
      const label = window ? `${name} (${window}${isFlexi ? ", flexi" : ""})` : name;
      return { id: String(s.id ?? name), name, label };
    })
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name));
}

const SHIFT_WORKFLOW_ROLE_LABELS = {
  manager: "Manager",
  hr: "HR",
  reporting_manager: "Reporting Manager",
  admin: "Admin",
};

/**
 * Desk pendingWith for a Change Shift request.
 * Uses the *new* shift's override workflow when configured; else Reporting Manager.
 * Note: multi-step progression is not fully automated yet — Approvals desk approves one step.
 */
export function resolveChangeShiftPendingWith(newShiftName) {
  const name = String(newShiftName || "").trim().toLowerCase();
  if (!name) return "Reporting Manager";
  const shift = loadAttendanceShifts().find(
    (s) => String(s?.name || "").trim().toLowerCase() === name
  );
  if (!shift?.allowShiftOverride || !shift?.shiftOverrideRequiresApproval) {
    return "Reporting Manager";
  }
  const steps = Array.isArray(shift.shiftOverrideWorkflowSteps)
    ? shift.shiftOverrideWorkflowSteps
    : [];
  const first = steps.find((s) => s && !s.notifyOnly);
  if (!first?.role) return "Reporting Manager";
  return (
    SHIFT_WORKFLOW_ROLE_LABELS[first.role] ||
    String(first.role).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

export const SHIFT_OVERRIDE_STORAGE_KEY = "hrms_attendance_shift_override_v1";
export const SHIFT_OVERRIDE_UPDATED_EVENT = "hrms-attendance-shift-override-updated";

function loadAll() {
  try {
    const raw = localStorage.getItem(SHIFT_OVERRIDE_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveAll(rows) {
  localStorage.setItem(SHIFT_OVERRIDE_STORAGE_KEY, JSON.stringify(rows));
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(SHIFT_OVERRIDE_UPDATED_EVENT));
  }
}

export function getShiftOverrideForDay(employeeId, dayKey = getLocalDayKey()) {
  const id = String(employeeId ?? "");
  if (!id) return null;
  const row = loadAll().find(
    (r) => String(r.employeeId) === id && r.dayKey === dayKey
  );
  if (!row) return null;
  const shifts = loadShiftsLocal();
  const shift =
    shifts.find((s) => String(s.id) === String(row.shiftId)) ||
    shifts.find(
      (s) =>
        String(s.name || "").toLowerCase() ===
        String(row.shiftName || "").toLowerCase()
    );
  return shift ? { ...row, shift } : row;
}

export function setShiftOverrideForDay(employeeId, shiftId, shiftName, dayKey = getLocalDayKey()) {
  const id = String(employeeId ?? "");
  if (!id || !shiftId) return null;
  const next = loadAll().filter(
    (r) => !(String(r.employeeId) === id && r.dayKey === dayKey)
  );
  const entry = {
    employeeId: id,
    dayKey,
    shiftId: String(shiftId),
    shiftName: shiftName || "",
    setAt: Date.now(),
  };
  next.push(entry);
  saveAll(next);
  return entry;
}

export function clearShiftOverrideForDay(employeeId, dayKey = getLocalDayKey()) {
  const id = String(employeeId ?? "");
  saveAll(
    loadAll().filter((r) => !(String(r.employeeId) === id && r.dayKey === dayKey))
  );
}

export function listShiftOverridesForEmployee(employeeId) {
  return loadAll().filter((r) => String(r.employeeId) === String(employeeId));
}

/**
 * Approved Change Shift request for this employee on/before dayKey (YYYY-MM-DD).
 * Used when evaluating PR/LOP after roster changes (same idea as attendance calendar).
 */
export function resolveApprovedChangeShiftName(employeeId, dayKey = getLocalDayKey()) {
  try {
    const raw = localStorage.getItem("adminQuickActionRequests");
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return null;
    const targetEmpId = Number(employeeId);
    const approved = parsed
      .filter((r) => {
        if (!r || r.actionLabel !== "Change Shift" || r.status !== "Approved") return false;
        const reqEmpId = Number(r.employeeId);
        if (!Number.isFinite(reqEmpId) || !Number.isFinite(targetEmpId) || reqEmpId !== targetEmpId) {
          return false;
        }
        const effectiveDate = String(r?.formData?.date || "").trim();
        const shiftName = String(r?.formData?.shift || "").trim();
        if (!effectiveDate || !shiftName) return false;
        return effectiveDate <= dayKey;
      })
      .sort((a, b) => {
        const ad = String(a?.formData?.date || "");
        const bd = String(b?.formData?.date || "");
        if (ad !== bd) return bd.localeCompare(ad);
        return Number(b?.submittedAt || 0) - Number(a?.submittedAt || 0);
      });
    if (!approved.length) return null;
    return String(approved[0]?.formData?.shift || "").trim() || null;
  } catch {
    return null;
  }
}

/** Prefer local day override, else approved Change Shift name. */
export function resolveShiftNameHintForDay(employeeId, dayKey = getLocalDayKey()) {
  const override = getShiftOverrideForDay(employeeId, dayKey);
  if (override?.shiftName) return override.shiftName;
  if (override?.shift?.name) return override.shift.name;
  return resolveApprovedChangeShiftName(employeeId, dayKey);
}

/** Best-effort current shift name for an employee (override → approved Change Shift → default/General). */
export function resolveDisplayCurrentShiftName(employeeId, dayKey = getLocalDayKey()) {
  const hinted = resolveShiftNameHintForDay(employeeId, dayKey);
  if (hinted) return hinted;
  const shifts = loadAttendanceShifts();
  const def =
    shifts.find((s) => s?.isDefault) ||
    shifts.find((s) => String(s?.name || "").trim().toLowerCase() === "general") ||
    shifts[0];
  return def?.name ? String(def.name).trim() : "";
}
